import { faCheckCircle } from "@fortawesome/free-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import ArrowDropDownIcon from "@mui/icons-material/ArrowDropDown";
import {
    Autocomplete,
    Box,
    Button,
    CircularProgress,
    Dialog,
    DialogActions,
    DialogContent,
    DialogContentText,
    DialogTitle,
    FormControl,
    InputAdornment,
    MenuItem,
    Select,
    TextField
} from "@mui/material";
import { useDashboard } from "context/DashboardContext";
import { useEffect, useState } from "react";

// Custom Select component
const CustomSelect = ({
  label,
  name,
  value,
  onChange,
  options,
  loading,
  disabled,
  placeholder,
}) => (
  <Box mb={2}>
    <span style={{ fontSize: "13px" }}>{label}</span>
    <FormControl fullWidth>
      <Select
        name={name}
        value={value}
        onChange={onChange}
        startAdornment={
          <InputAdornment position='start'>
            <ArrowDropDownIcon style={{ color: "black", fontSize: "2rem" }} />
          </InputAdornment>
        }
        displayEmpty
        disabled={disabled}
      >
        <MenuItem value='' disabled>
          {loading ? <CircularProgress size={24} /> : placeholder}
        </MenuItem>
        {options.map((option, index) => (
          <MenuItem key={index} value={option.value}>
            {option.label}
          </MenuItem>
        ))}
      </Select>
    </FormControl>
  </Box>
);

const SearchableMerchantSelect = ({
  label,
  value,
  onChange,
  options,
  loading,
  disabled,
  placeholder,
}) => {
  const selectedOption =
    options.find((option) => String(option.value) === String(value)) || null;

  return (
    <Box mb={2}>
      <span style={{ fontSize: "13px" }}>{label}</span>
      <Autocomplete
        options={options}
        value={selectedOption}
        onChange={(_, option) => onChange(option ? option.value : "")}
        loading={loading}
        disabled={disabled}
        getOptionLabel={(option) => option?.label || ""}
        isOptionEqualToValue={(option, selected) =>
          String(option.value) === String(selected.value)
        }
        renderInput={(params) => (
          <TextField
            {...params}
            placeholder={placeholder}
            fullWidth
            InputProps={{
              ...params.InputProps,
              endAdornment: (
                <>
                  {loading ? <CircularProgress color='inherit' size={18} /> : null}
                  {params.InputProps.endAdornment}
                </>
              ),
            }}
          />
        )}
        renderOption={(props, option) => (
          <li {...props} key={option.value}>
            {option.label}
          </li>
        )}
      />
    </Box>
  );
};

// Custom TextField component
const CustomTextField = ({ label, name, value, onChange }) => (
  <Box mb={2}>
    <span style={{ fontSize: "13px" }}>{label}</span>
    <TextField
      name={name}
      placeholder={label}
      value={value}
      onChange={onChange}
      fullWidth
    />
  </Box>
);

export default function CreateCharge({
  isOpen,
  handleClose,
  rowData,
  refresh,
}) {
  const {
    allMerchants,
    setMerchantCharges,
    loading,
    refreshData,
    chargeError,
  } = useDashboard();

  const [state, setState] = useState({
    merchant: "",
    fee: "",
    bearer: "MERCHANT",
    mode: "",
    maxAmount: "",
    showSuccess: false,
    modeDisplay: false,
    // chargeCard: true,
    // cardFee: "",
    // cardFeeBearer: "CUSTOMER",
  });

  useEffect(() => {
    if (rowData) {
      setState((prevState) => ({
        ...prevState,
        merchant: rowData.merchant.id || "",
        fee: rowData.merchant.fee || "",
        bearer: rowData.merchant.feeBearer || "MERCHANT",
        modeDisplay: rowData.merchant.feeByPercentage || false,
        mode: rowData.merchant.feeByPercentage ? "Percentage" : "Fixed",
        maxAmount: rowData.merchant.maxFeeAmount || "",
        // chargeCard: rowData.merchant.chargeCard || true,
        // cardFee: rowData.merchant.cardFee || "",
        // cardFeeBearer: rowData.merchant.cardFeeBearer || "",
      }));
    }
  }, [rowData]);

  const handleInputChange = (event) => {
    const { name, value, type, checked } = event.target;
    setState((prevState) => ({
      ...prevState,
      [name]: type === "checkbox" ? checked : value,
      modeDisplay:
        name === "mode" ? value === "Percentage" : prevState.modeDisplay,
    }));
  };

  const handleSave = async () => {
    const payload = {
      id: rowData ? rowData.merchant.id : state.merchant,
      feeByPercentage: state.mode === "Percentage",
      maxFeeAmount: parseFloat(state.maxAmount) || 0,
      fee: parseFloat(state.fee) || 0,
      feeBearer: state.bearer,
      // chargeCard: state.chargeCard,
      // cardFee: parseFloat(state.cardFee) || 0,
      // cardFeeBearer: state.cardFeeBearer,
      applyDefaultCharge: false,
    };

    try {
      await setMerchantCharges(payload); // Updating the merchant charge via context API

      setState((prevState) => ({ ...prevState, showSuccess: true }));

      setTimeout(() => {
        setState((prevState) => ({ ...prevState, showSuccess: false }));
        handleClose();
        refreshData();
      }, 3000);
    } catch (error) {
      console.error(
        "Error saving charge data:",
        error.response || error.message
      );
    }
  };

  // useEffect(() => {
  //   if (state.showSuccess) {
  //     refresh();
  //   }
  // }, [state.showSuccess]);

  const {
    merchant,
    fee,
    bearer,
    mode,
    maxAmount,
    showSuccess,
    modeDisplay,
    // chargeCard,
    // cardFee,
    // cardFeeBearer,
  } = state;

  return (
    <Dialog
      open={isOpen}
      onClose={handleClose}
      PaperProps={{
        style: { borderRadius: "10px", padding: "1rem", width: "100%" },
      }}
    >
      <DialogTitle>{rowData ? `Edit` : `Create`} Charge</DialogTitle>
      <DialogContent>
        {showSuccess ? (
          <Box textAlign='center'>
            <FontAwesomeIcon
              icon={faCheckCircle}
              style={{ color: "green", fontSize: "4rem", marginBottom: "1rem" }}
            />
            <DialogContentText>
              Charge {rowData ? `updated` : `created`} successfully!
            </DialogContentText>
          </Box>
        ) : (
          // ✅ FIX: DialogContentText renders a <p>, so don't put div/fieldset inside it.
          <Box>
            {chargeError && <Box color='red'>{chargeError}</Box>}
            <SearchableMerchantSelect
              label={"Select Merchant:"}
              value={merchant}
              onChange={(value) => {
                setState((prevState) => ({ ...prevState, merchant: value }));
              }}
              options={allMerchants.map((merchant) => ({
                value: merchant.merchant.id,
                label: `${merchant.merchant.name} (${merchant.merchant.id})`,
              }))}
              loading={loading}
              disabled={!!rowData}
              placeholder={rowData ? rowData.merchant.name : "Select Merchant"}
            />
            <CustomTextField
              label='Set Fee:'
              name='fee'
              value={fee}
              onChange={handleInputChange}
            />
            <CustomSelect
              label='Select Bearer:'
              name='bearer'
              value={bearer}
              onChange={handleInputChange}
              placeholder='Select Bearer'
              options={[
                { value: "MERCHANT", label: "Merchant" },
                { value: "CUSTOMER", label: "Customer" },
              ]}
            />
            {/* <FormControlLabel
              control={
                <Checkbox
                  checked={chargeCard}
                  onChange={handleInputChange}
                  name='chargeCard'
                />
              }
              label='Charge Card'
            /> */}
            {/* {chargeCard && (
              <>
                <CustomTextField
                  label='Card Fee:'
                  name='cardFee'
                  value={cardFee}
                  onChange={handleInputChange}
                />
                <CustomSelect
                  label='Card Fee Bearer:'
                  name='cardFeeBearer'
                  value={cardFeeBearer}
                  onChange={handleInputChange}
                  placeholder='Select Bearer'
                  options={[
                    { value: "MERCHANT", label: "Merchant" },
                    { value: "CUSTOMER", label: "Customer" },
                  ]}
                />
              </>
            )} */}
            <CustomSelect
              label='Select Mode:'
              name='mode'
              value={mode}
              onChange={handleInputChange}
              placeholder='Select Mode'
              options={[
                { value: "Percentage", label: "Percentage" },
                { value: "Fixed", label: "Fixed" },
              ]}
            />
            {modeDisplay && (
              <CustomTextField
                label='Set Max Amount:'
                name='maxAmount'
                value={maxAmount}
                onChange={handleInputChange}
              />
            )}
          </Box>
        )}
      </DialogContent>
      {!showSuccess && (
        <DialogActions>
          <Button onClick={handleClose} className='small-button'>
            Close
          </Button>
          <Button onClick={handleSave} className='small-button'>
            {rowData ? `Save` : `Create`}
          </Button>
        </DialogActions>
      )}
    </Dialog>
  );
}
